import React, { useEffect, Fragment, useState, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Breadcrumb from "../../../../components/common/breadcrumb";
import {
  GetIpdEntries,
} from "../../../../api/ipd/ipdEntryAPI";
import { GetLastPaymentReceive, StorePaymentReceive } from "../../../../api/ipd/paymentReceiveAPI";
import { GetLastEntry } from "../../../../api/common/common";
import useKeyboardSubmit from "../../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../../hooks/useEnterNavigation";
import { GetDepartmentalSubSpecialities } from "../../../../api/diagnosis/master/departmentalSubSpecilityAPI";
import { GetLabRegistrations } from "../../../../api/diagnosis/transaction/labRegistrationAPI";
import { GetPatientDischarges } from "../../../../api/ipd/patientDischargeAPI";

// Validation schema (basic example — you can expand this)
const schema = yup.object().shape({
  type: yup.string().required("Type is required"),
  ipdEntryId: yup.string().when("type", {
    is: "IPD",
    then: (schema) => schema.required("IPD Entry is required"),
    otherwise: (schema) => schema.nullable()
  }),
  patientDischargeId: yup.string().when("type", {
    is: "Discharge",
    then: (schema) => schema.required("Discharge Entry is required"),
    otherwise: (schema) => schema.nullable()
  }),
  subSpecialityId: yup.string().when("type", {
    is: "Investigation",
    then: (schema) => schema.required("Sub Speciality is required"),
    otherwise: (schema) => schema.nullable()
  }),
  labRegistrationId: yup.string().when("type", {
    is: "Investigation",
    then: (schema) => schema.required("Lab No is required"),
    otherwise: (schema) => schema.nullable()
  }),
  receiptNo: yup.string().required("Receipt number is required"),
  date: yup.date().required("Date is required"),
  time: yup.string().required("Time is required"),
  paymentMode: yup.string().required("Payment mode is required"),
  amount: yup
    .number()
    .typeError("Amount must be a number")
    .required("Amount is required")
    .min(0.01, "Amount must be greater than 0"),
  remark: yup.string().nullable(),
  debitAccount: yup.string().nullable()
});


const AddPaymentReceiveEntry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [ipdEntries, setIpdEntries] = useState([]);
  const [patientDischarges,setPatientDischarges] = useState([]);
  const [subSpecialities, setSubSpecialities] = useState([]);
  const [labRegistrations, setLabRegistrations] = useState([]);
  const [patientDetails, setPatientDetails] = useState({});

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {}
  });

  const type = watch("type");
  const fetchIpdEntries = async () => {
    try {
      const response = await GetIpdEntries();
      if (response.success) {
        setIpdEntries(response.data);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const fetchSubSpeciality = async () => {
    try {
      const response = await GetDepartmentalSubSpecialities();
      if (response.success) {
        setSubSpecialities(response.data);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const fetchLabRegistrations = async () => {
    try {
      const response = await GetLabRegistrations();
      if (response.success) {
        setLabRegistrations(response.data);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    }
  };
 
  const fetchPatientDischarges = async () => {
    try {
      const response = await GetPatientDischarges();
      if (response.success) {
        setLabRegistrations(response.data);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    }
  };

  const fetchLastEntry = async () => {
    try {
      const response = await GetLastEntry();
      if (response.success) {
        setValue("receiptNo", response?.data?.lastPaymentReceiveEntry?.receiptNo ? response?.data?.lastPaymentReceiveEntry?.receiptNo + 1 : 1);

      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    }
  };

  useEffect(() => {
    fetchIpdEntries();
    fetchSubSpeciality();
    fetchLabRegistrations();
    fetchPatientDischarges();
    fetchLastEntry();
  }, []);

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });
      const response = await StorePaymentReceive(data);
      if (response.success) {
        showToast("success", response.message);
        navigate(
          `/ipd/transaction/payment-receive-entry/list`
        );
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
  useKeyboardSubmit(handleSubmit, onSubmit);
  useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="Payment Receive" title="Add Payment Receive Entry" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Add Payment Receive Entry</h5>
                <Link
                  to={`/ipd/transaction/payment-receive-entry/list`}
                >
                  <button className="btn btn-primary" id="list">
                    List Payment Receive Entry
                  </button>
                  <UncontrolledTooltip placement="top" target="list">
                    List Payment Receive Entry
                  </UncontrolledTooltip>
                </Link>
              </div>

              <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label>Type *</label>
                      <select className="form-control" {...register("type")} >
                        <option value="">Select Type</option>
                        <option value="IPD">IPD</option>
                        <option value="Discharge">Discharge</option>
                        <option value="Investigation">Investigation</option>
                      </select>
                      {errors.type &&
                        <p className="text-danger">
                          {errors.type.message}
                        </p>}
                    </div>
                    {
                      type == "Investigation" &&

                      <>
                        <div className="col-md-4 mb-3">
                          <label>Sub Speciality *</label>
                          <select
                            className="form-control"
                            {...register("subSpecialityId")}
                          >
                            <option value="">Select Sub Speciality</option>
                            {subSpecialities.map(item => (
                              <option key={item.id} value={item.id}>
                                {item.name}
                              </option>
                            ))}
                          </select>
                          {errors.subSpecialityId &&
                            <p className="text-danger">
                              {errors.subSpecialityId.message}
                            </p>}
                        </div>

                        <div className="col-md-4 mb-3">
                          <label>Lab No *</label>
                          <select type="number" className="form-control" {...register("labRegistrationId")} >
                            <option value="">Select Lab No</option>
                            {labRegistrations.map(item => (
                              <option key={item._id} value={item._id}>
                                {item.labNo}
                              </option>
                            ))}
                          </select>
                          {errors.labRegistrationId &&
                            <p className="text-danger">
                              {errors.labRegistrationId.message}
                            </p>}
                        </div>
                      </>
                    }
                    {
                      type == "IPD" &&
                      <div className="col-md-4 mb-3">
                        <label>IPD No *</label>
                        <select
                          className="form-control"
                          {...register("ipdEntryId", {
                            onChange: (e) => {
                              setPatientDetails(ipdEntries.find(ipd => ipd._id === e.target.value));
                            }
                          })}
                        >
                          <option value="">Select</option>
                          {ipdEntries.map((ipdEntry, index) => (
                            <option key={index} value={ipdEntry._id}>
                              {ipdEntry.ipdNo}
                            </option>
                          ))}
                        </select>
                        {errors.ipdEntryId &&
                          <p className="text-danger">
                            {errors.ipdEntryId.message}
                          </p>}
                      </div>

                    }
                    {
                      type == "Discharge" &&
                      <div className="col-md-4 mb-3">
                        <label>IPD No *</label>
                        <select
                          className="form-control"
                          {...register("patientDischargeId", {
                            onChange: (e) => {
                              setPatientDetails(patientDischarges.find(pd => pd._id === e.target.value)?.ipdEntryId);
                            }
                          })}
                        >
                          <option value="">Select</option>
                          {patientDischarges.map((pDischarge, index) => (
                            <option key={index} value={pDischarge._id}>
                              {pDischarge.ipdEntryId?.ipdNo}
                            </option>
                          ))}
                        </select>
                        {errors.patientDischargeId &&
                          <p className="text-danger">
                            {errors.patientDischargeId.message}
                          </p>}
                      </div>

                    }
                  </div>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label>Receipt No *</label>
                      <input
                        className="form-control"
                        {...register("receiptNo")}
                        readOnly
                      />
                      {errors.receiptNo &&
                        <p className="text-danger">
                          {errors.receiptNo.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        defaultValue={new Date().toISOString().slice(0, 10)}
                        min={new Date().toISOString().slice(0, 10)}
                        {...register("date")}
                      />
                      {errors.date &&
                        <p className="text-danger">
                          {errors.date.message}
                        </p>}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label>Time *</label>
                      <input
                        type="time"
                        defaultValue={new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}

                        className="form-control"
                        {...register("time")}
                      />
                      {errors.time &&
                        <p className="text-danger">
                          {errors.time.message}
                        </p>}
                    </div>

                    <div className="col-12">
                      <h5 className="mt-3 mb-3 text-primary">
                        Patient Details
                      </h5>
                    </div>
                    <div className="row">
                      <div className="col-6">
                        <div className="form-group">
                          <label>UHID No</label>
                          <input
                            className="form-control"
                            placeholder="UHID No"
                            value={patientDetails?.patientId?.uhidNo}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="form-group">
                          <label>Ipd No</label>
                          <input
                            className="form-control"
                            placeholder="Ipd No"
                            value={patientDetails?.ipdNo}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="form-group">
                          <label>Patient</label>
                          <input
                            className="form-control"
                            placeholder="Patient Name"
                            value={patientDetails?.patientId?.patientName}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="form-group">
                          <label>Guardian Name</label>
                          <input
                            className="form-control"
                            placeholder="Guardian Name"
                            value={patientDetails?.patientId?.guardianName}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="form-group">
                          <label>Age</label>
                          <input
                            className="form-control"
                            placeholder="Patient Name"
                            value={patientDetails?.patientId?.age}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="form-group">
                          <label>Case</label>
                          <input
                            className="form-control"
                            placeholder="Case"
                            value={patientDetails?.caseId?.name}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="form-group">
                          <label>Consultant Doctor</label>
                          <input
                            className="form-control"
                            placeholder="Consultant Doctor"
                            value={patientDetails?.consultantDoctorId?.name}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="form-group">
                          <label>ReferenceDoctor</label>
                          <input
                            className="form-control"
                            placeholder="ReferenceDoctor"
                            value={patientDetails?.referenceDoctorId?.name}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="form-group">
                          <label>Address</label>
                          <input
                            className="form-control"
                            placeholder="Address"
                            value={patientDetails?.patientId?.address}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Payment Mode *</label>
                      <select
                        className="form-control"
                        {...register("paymentMode")}
                      >
                        <option value="Cash">Cash</option>
                        <option value="Online">Online</option>
                      </select>
                      {errors.paymentMode &&
                        <p className="text-danger">
                          {errors.paymentMode.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Amount *</label>
                      <input
                        type="number"
                        className="form-control"
                        {...register("amount")}
                      />
                      {errors.amount &&
                        <p className="text-danger">
                          {errors.amount.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Remark</label>
                      <input className="form-control" {...register("remark")} />
                      {errors.remark &&
                        <p className="text-danger">
                          {errors.remark.message}
                        </p>}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label>Debit Account</label>
                      <input
                        className="form-control"
                        {...register("debitAccount")}
                      />
                      {errors.debitAccount &&
                        <p className="text-danger">
                          {errors.debitAccount.message}
                        </p>}
                    </div>
                  </div>
                </div>

                <div className="card-footer text-end">
                  <button className="btn btn-primary me-2" type="submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddPaymentReceiveEntry;
